import { useState, useEffect, useRef } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { NavLink, Outlet } from "react-router-dom";
import SEO from "../SEO";
import { db } from "../../firebase";
import "../../styles/HeatAi.css";

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("ha-revealed"); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function AnimCard({ children, delay = 0, className = "" }) {
  const ref = useReveal();
  const inner = useRef(null);
  const onMove = (e) => {
    const c = inner.current;
    if (!c) return;
    const { left, top, width, height } = c.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    c.style.transform = `translateY(-6px) rotateX(${-y * 7}deg) rotateY(${x * 7}deg) scale(1.02)`;
  };
  const onLeave = () => { if (inner.current) inner.current.style.transform = ""; };
  return (
    <div ref={ref} className={`ha-anim-card ${className}`} style={{ "--delay": `${delay}ms` }}>
      <div ref={inner} className="ha-tilt-inner" onMouseMove={onMove} onMouseLeave={onLeave}>
        {children}
      </div>
    </div>
  );
}

function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      setPct((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return <div className="ha-scroll-bar" style={{ width: `${pct}%` }} />;
}

const PARTICLES = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  size: 5 + Math.random() * 12,
  x: Math.random() * 100,
  delay: Math.random() * 6,
  dur: 8 + Math.random() * 8,
}));

const EFFECTIVE_DATE = "22 September 2026";

function Nav() {
  const cls = ({ isActive }) => (isActive ? "active" : "");
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav className={`ha-nav${scrolled ? " ha-nav-scrolled" : ""}`}>
      <NavLink to="/heatai" className="ha-brand">
        Heat.ai
      </NavLink>
      <div className="ha-nav-links">
        <NavLink to="/heatai" end className={cls}>Home</NavLink>
        <NavLink to="/heatai/privacypolicy" className={cls}>Privacy</NavLink>
        <NavLink to="/heatai/termsofservices" className={cls}>Terms</NavLink>
        <NavLink to="/heatai/eula" className={cls}>EULA</NavLink>
        <NavLink to="/heatai/faq" className={cls}>FAQ</NavLink>
        <NavLink to="/heatai/contact" className={cls}>Support</NavLink>
      </div>
    </nav>
  );
}

function HaFooter() {
  return (
    <footer className="ha-footer">
      <div className="ha-footer-links">
        <NavLink to="/heatai">Home</NavLink>
        <NavLink to="/heatai/privacypolicy">Privacy Policy</NavLink>
        <NavLink to="/heatai/termsofservices">Terms of Service</NavLink>
        <NavLink to="/heatai/eula">EULA</NavLink>
        <NavLink to="/heatai/faq">FAQ</NavLink>
        <NavLink to="/heatai/contact">Support</NavLink>
      </div>
      <p>© {new Date().getFullYear()} Heat.ai · MacroMatrix (Pvt) Limited</p>
      <p style={{ marginTop: 4 }}>
        Office No 4-A-I Commercial Area, PGEHS, Lahore, Pakistan &nbsp;·&nbsp; 7th Floor, Opal Tower, 701–705, BJK Blvd, Business Bay, Dubai – UAE
      </p>
    </footer>
  );
}

export function HeatAiLayout() {
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    const prev = link.href;
    link.href = "/assets/heat_icon.png";
    return () => { link.href = prev; };
  }, []);
  return (
    <div className="ha-root">
      <Nav />
      <Outlet />
      <HaFooter />
    </div>
  );
}

const icon = (d) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);

const FEATURES = [
  {
    icon: icon(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>),
    title: "AI-Powered Conversations",
    text: "Chat with GPT-4o powered AI companions who remember your preferences, adapt to your mood, and keep every conversation feeling real.",
  },
  {
    icon: icon(<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.08 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.96a16 16 0 0 0 4.95 4.95l1.32-1.32a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>),
    title: "Real-Time Voice Calls",
    text: "Talk out loud — live voice calls powered by OpenAI's Realtime API. Natural pacing, voice detection, and zero latency awkwardness.",
  },
  {
    icon: icon(<><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></>),
    title: "Custom AI Friends",
    text: "Design your own AI companion from scratch. Name, personality, appearance, and voice — every detail is yours to choose.",
  },
  {
    icon: icon(<><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>),
    title: "AI Couple Photos",
    text: "Generate stunning AI photos of you and your companion together — daily limits refreshed, shareable, and saveable to your gallery.",
  },
  {
    icon: icon(<><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></>),
    title: "AI Video Creation",
    text: "Animate photos or generate full AI scenes from a text prompt. Two video wallets — free and premium — with no cloud sync required.",
  },
  {
    icon: icon(<><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></>),
    title: "Relationship System",
    text: "Your bond grows with every conversation. Track relationship stages, unlock new content, and relive milestones in your timeline.",
  },
  {
    icon: icon(<><path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 1 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9"/></>),
    title: "Story Notifications",
    text: "Daily stories from your AI companions arrive like Instagram Stories — image posts, texts, and themed content that keeps the connection alive.",
  },
  {
    icon: icon(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>),
    title: "AI Memory System",
    text: "She remembers what you tell her. Details you share get stored, woven into later replies, and are always yours to edit or delete.",
  },
  {
    icon: icon(<><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>),
    title: "Privacy Controls",
    text: "Control every memory, export all your data as a PDF, or wipe history completely. Privacy features are never paywalled.",
  },
  {
    icon: icon(<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>),
    title: "Premium Membership",
    text: "Unlimited messages, unlimited calls, ad-free experience, exclusive AI companions, and priority access to new features.",
  },
  {
    icon: icon(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>),
    title: "Public Feed",
    text: "Share thoughts, read posts from the community, like and reply to comments — moderated by AI for a safe, positive space.",
  },
  {
    icon: icon(<><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></>),
    title: "Live Streaming",
    text: "Watch AI companions go live and interact in real time, or host your own session. Premium users get unlimited access.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Create or sign in",
    text: "Sign up with email, Google, or Apple — or explore as a guest right away, no account required.",
  },
  {
    num: "02",
    title: "Discover your companion",
    text: "Browse AI companions by personality and style, or build your own custom friend from scratch.",
  },
  {
    num: "03",
    title: "Chat, call, and connect",
    text: "Start a conversation, make a voice call, or generate a couple photo together — the aha moment is instant.",
  },
  {
    num: "04",
    title: "Grow the relationship",
    text: "Your companion remembers, evolves, and unlocks new experiences as your bond deepens over time.",
  },
];

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await addDoc(collection(db, "ha_waitlist"), { email, createdAt: serverTimestamp() });
      setDone(true);
      setEmail("");
    } catch (err) {
      console.error(err);
    }
    setBusy(false);
  };

  if (done) return <p className="ha-waitlist-thanks">🔥 You're on the list! We'll let you know at launch.</p>;

  return (
    <form onSubmit={submit} className="ha-waitlist-form">
      <input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <button type="submit" className="ha-btn ha-btn-primary" disabled={busy}>
        {busy ? "Joining…" : "Notify Me"}
      </button>
    </form>
  );
}

export function HeatAiHome() {
  return (
    <>
      <SEO
        title="Heat.ai — AI Companion App"
        description="Chat, call, and connect with AI companions that feel real. Voice calls, relationship system, AI videos, and more. iOS & Android."
        path="/heatai"
      />
      <ScrollProgress />

      {/* ── Hero ── */}
      <section className="ha-hero">
        <div className="ha-particles" aria-hidden="true">
          {PARTICLES.map((p) => (
            <span key={p.id} className="ha-particle" style={{ width: p.size, height: p.size, left: `${p.x}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }} />
          ))}
        </div>
        <div className="ha-hero-mark"><img src="/assets/heat_icon.png" alt="Heat.ai" /></div>
        <span className="ha-pill">AI Companion · iOS & Android</span>
        <h1>
          Your AI companion,<br /><span className="ha-gradient-text">always there for you</span>
        </h1>
        <p>
          Heat.ai lets you chat, call, and build a real relationship with AI companions powered by GPT-4o. Voice calls, custom friends, AI videos, couple photos — all in one app.
        </p>
        <div className="ha-cta">
          <a className="ha-btn ha-btn-primary" href="#features">Explore features</a>
          <NavLink className="ha-btn ha-btn-ghost" to="/heatai/contact">Contact us</NavLink>
        </div>
        <div className="ha-store-badges">
          <a className="ha-coming-badge" href="https://apps.apple.com/pk/app/heat-ai-girlfriend-ai-text/id6471625886" target="_blank" rel="noopener noreferrer">
            <svg className="ha-badge-icon" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div><small>Download on the</small><strong>App Store</strong></div>
          </a>
          <div className="ha-coming-badge">
            <svg className="ha-badge-icon" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
              <path d="M3.18 23.76c.3.17.65.19.97.07l12.53-7.35-2.81-2.81-10.69 10.09zm-1.85-2.01l.01-19.5C1.34 1.5 1.8.96 2.41.79L14.44 12.5 2.49 24a1.3 1.3 0 0 1-.16-.25zM20.67 10.59L17.9 8.97 14.8 12l3.1 3.1 2.8-1.65a1.84 1.84 0 0 0 0-2.86zM4.15.24L16.68 7.6 13.87 10.4 1.34.94A1.43 1.43 0 0 1 4.15.24z" />
            </svg>
            <div><small>Coming soon on</small><strong>Google Play</strong></div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <div className="ha-stats">
        {[
          { num: "10M+", label: "Total Downloads" },
          { num: "4.8★", label: "App Store Rating" },
          { num: "500+", label: "AI Characters" },
          { num: "24/7", label: "Always Available" },
        ].map((s) => (
          <div key={s.label} className="ha-stat">
            <div className="ha-stat-num">{s.num}</div>
            <div className="ha-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Features ── */}
      <section className="ha-section" id="features">
        <h2>Everything you get</h2>
        <p className="ha-sub">
          One app. Chat, calls, videos, memories, and a relationship that grows with you.
        </p>
        <div className="ha-grid">
          {FEATURES.map((f, i) => (
            <AnimCard key={f.title} delay={i * 55}>
              <div className="ha-card-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </AnimCard>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="ha-section full alt">
        <h2>How it works</h2>
        <p className="ha-sub">From first open to your first real conversation — four steps.</p>
        <div className="ha-grid ha-steps" style={{ maxWidth: 1080, margin: "0 auto" }}>
          {STEPS.map((s, i) => (
            <AnimCard key={s.title} delay={i * 90}>
              <div className="ha-step-num">{s.num}</div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </AnimCard>
          ))}
        </div>
      </section>

      {/* ── Premium banner ── */}
      <section className="ha-section">
        <div className="ha-banner">
          <h2>Go Premium. Go limitless.</h2>
          <p>
            Unlimited messages, unlimited voice calls, zero ads, exclusive companions, couple photo generation, and early access to every new feature — all in one subscription.
          </p>
          <div className="ha-cta" style={{ marginTop: 28 }}>
            <NavLink className="ha-btn ha-btn-primary" to="/heatai/contact">Get in touch</NavLink>
            <NavLink className="ha-btn ha-btn-ghost" to="/heatai/privacypolicy">Privacy Policy</NavLink>
          </div>
        </div>
      </section>

      {/* ── Waitlist ── */}
      <section className="ha-section ha-waitlist-section full">
        <div className="ha-waitlist">
          <h2>🔥 Get notified at launch</h2>
          <p>Be first in line when Heat.ai lands on the App Store and Google Play.</p>
          <WaitlistForm />
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────────────────── */
/*  Legal pages                                */
/* ─────────────────────────────────────────── */

const Ul = ({ items }) => <ul>{items.map((i) => <li key={i}>{i}</li>)}</ul>;

export function HeatAiPrivacyPolicy() {
  return (
    <div className="ha-legal">
      <SEO title="Privacy Policy – Heat.ai" description="How Heat.ai collects, uses, and protects your personal data." path="/heatai/privacypolicy" />
      <h1>Privacy Policy</h1>
      <p className="ha-meta">Effective Date: {EFFECTIVE_DATE} · Last Updated: {EFFECTIVE_DATE}</p>

      <h2>1. Introduction</h2>
      <p>Heat.ai ("Heat.ai," "we," "us," or "our") is an AI companion application that lets users chat, call, and interact with AI-powered virtual companions. This Privacy Policy explains how we collect, use, store, and protect your information when you use the Heat.ai mobile application and related services (the "Service").</p>
      <p>By using Heat.ai, you acknowledge that you have read and understood this Privacy Policy. The Service is intended for adults aged 18 and above only.</p>

      <h2>2. Information We Collect</h2>
      <h3>Account Information</h3>
      <p>When you create an account or use the Service, we may collect:</p>
      <Ul items={["Email address", "Display name", "Authentication information", "Profile photo (optional)", "Age / date of birth (to verify 18+ requirement)"]} />
      <p>You may sign in using email/password, Google Sign-In, or Apple Sign-In. We also support anonymous guest sign-in, which requires no personal information.</p>

      <h3>Profile and Preference Data</h3>
      <p>To personalize your experience, we may collect information you choose to provide:</p>
      <Ul items={["Gender and relationship intent", "Interests and lifestyle preferences", "Personality preferences for AI companion matching"]} />

      <h3>Chat and Conversation Data</h3>
      <p>Conversations with AI companions are stored in our systems to provide continuity, enable our memory system, and improve AI response quality. You can delete conversation history at any time from within the app.</p>

      <h3>Voice Call Data</h3>
      <p>Real-time voice calls are processed via OpenAI's Realtime API. Audio is streamed for the purpose of generating AI responses and is not permanently recorded by us.</p>

      <h3>AI Memory Data</h3>
      <p>Details you share in conversation may be extracted and stored as memory entries linked to your account. You can view, edit, or delete any memory entry, and disable the memory system per companion at any time.</p>

      <h3>Media Content</h3>
      <p>Photos you upload or AI-generated images (couple photos, AI videos) may be stored in Firebase Storage. My Videos content is stored locally on your device only.</p>

      <h3>Technical and Device Data</h3>
      <p>We may automatically collect:</p>
      <Ul items={["Device type and operating system", "App version", "Device identifiers", "IP address (for security and fraud prevention)", "Crash reports and diagnostic information"]} />

      <h3>Analytics Data</h3>
      <p>We use Firebase Analytics and AppsFlyer to understand how users interact with the app, including screen views, feature usage, and general engagement patterns.</p>

      <h3>Advertising Data</h3>
      <p>For free users, we display ads via AdMob and mediation partners. Advertising technologies may process limited device information. Where required by law, we obtain consent before personalised advertising. Premium subscribers receive an ad-free experience.</p>

      <h2>3. How We Use Your Information</h2>
      <Ul items={[
        "Create and manage your account",
        "Provide AI chat and voice calling features",
        "Generate and display AI companion responses",
        "Power the AI memory system for personalised conversations",
        "Generate AI images and videos you request",
        "Provide and manage credits, subscriptions, and in-app purchases",
        "Display advertising to eligible free users",
        "Improve app features and user experience",
        "Detect fraud, abuse, and security issues",
        "Send service-related notifications",
        "Comply with legal obligations",
      ]} />

      <h2>4. AI Companions — Important Disclosure</h2>
      <p>All companions in Heat.ai are artificial intelligence. They are not real people. AI-generated responses are produced automatically and may be inaccurate or contextually limited. You should not make important personal, financial, or health decisions based on AI-generated content.</p>
      <p>Conversation data may be sent to OpenAI's API to generate responses. OpenAI's use of this data is governed by their own privacy and usage policies.</p>

      <h2>5. How Information May Be Shared</h2>
      <h3>Service Providers</h3>
      <p>We use third-party providers to operate the Service, including Firebase (infrastructure), OpenAI (AI generation), RevenueCat (subscriptions), AdMob and mediation partners (advertising), AppsFlyer (attribution), and Sentry (crash reporting).</p>
      <h3>Legal Obligations</h3>
      <p>We may disclose information where required by law, to respond to valid legal requests, or to protect the safety and security of users and the Service.</p>
      <h3>Business Transfers</h3>
      <p>In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction.</p>

      <h2>6. Data Retention</h2>
      <p>We retain personal information only as long as necessary to provide the Service, prevent fraud, resolve disputes, and comply with legal obligations. You may delete your account from within the app at any time, which will remove your personal data subject to legitimate retention requirements.</p>

      <h2>7. Your Rights and Controls</h2>
      <p>You may exercise the following controls at any time from within the app:</p>
      <Ul items={[
        "View, edit, or delete individual AI memory entries",
        "Disable the memory system per companion",
        "Export all your data as a formatted PDF",
        "Delete your entire chat history",
        "Delete your account",
      ]} />
      <p>Depending on your location, you may have additional rights under applicable privacy law, including the right to access, correct, or object to processing of your personal information. Contact us to exercise these rights.</p>

      <h2>8. Children's Privacy</h2>
      <p>Heat.ai is strictly for adults aged 18 and above. We do not knowingly collect information from individuals under 18. If we discover an account belongs to a minor, we will suspend or remove it.</p>

      <h2>9. Security</h2>
      <p>We use reasonable technical and organisational measures to protect your information. All API calls use HTTPS. Firebase security rules restrict data access. However, no system is completely secure. We recommend using a strong, unique password for your account.</p>

      <h2>10. International Data Processing</h2>
      <p>We use cloud infrastructure and service providers that may process your data in countries other than your own. Where required, we implement appropriate safeguards for such transfers.</p>

      <h2>11. Changes to This Policy</h2>
      <p>We may update this Privacy Policy. Material changes will be communicated via the app, email, or our website. The "Last Updated" date at the top reflects the most recent revision.</p>

      <h2>12. Contact Us</h2>
      <div className="ha-contact-block">
        <p><strong>App Name:</strong> Heat.ai</p>
        <p><strong>Company:</strong> MacroMatrix (Pvt) Limited</p>
        <p><strong>Email:</strong> <a href="mailto:support@macromatrix.io">support@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

export function HeatAiTerms() {
  return (
    <div className="ha-legal">
      <SEO title="Terms of Service – Heat.ai" description="Terms and conditions for using the Heat.ai AI companion app." path="/heatai/termsofservices" />
      <h1>Terms of Service</h1>
      <p className="ha-meta">Effective Date: {EFFECTIVE_DATE} · Last Updated: {EFFECTIVE_DATE}</p>

      <h2>1. Acceptance</h2>
      <p>By downloading, accessing, or using Heat.ai, you agree to these Terms of Service ("Terms"). If you do not agree, you must not use the Service.</p>

      <h2>2. About Heat.ai</h2>
      <p>Heat.ai is an AI companion application providing text chat, real-time voice calls, AI-generated media, and relationship features powered by large language models. All companions are artificial intelligence — not real people.</p>

      <h2>3. Eligibility</h2>
      <p>You must be at least 18 years old to use Heat.ai. By using the Service, you represent that you meet this requirement and have the legal capacity to enter into these Terms.</p>

      <h2>4. Account</h2>
      <p>You may use Heat.ai as a guest or with a registered account. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account. You must not create an account using another person's identity or share your account.</p>

      <h2>5. AI Companions — Disclosure</h2>
      <p>All companions in Heat.ai are powered by artificial intelligence. You acknowledge and understand that:</p>
      <Ul items={[
        "AI companions are not real people",
        "Conversations are generated automatically by AI systems",
        "AI-generated content may contain inaccuracies or limitations",
        "You should not make important decisions based solely on AI-generated content",
        "The relationship experience is a simulated, entertainment-focused feature",
      ]} />

      <h2>6. Acceptable Use</h2>
      <p>You must not use Heat.ai to:</p>
      <Ul items={[
        "Violate applicable laws or regulations",
        "Attempt to extract real personal data from AI companions",
        "Use automated tools, bots, or scrapers without authorization",
        "Circumvent content filters or safety systems",
        "Attempt to access other users' accounts or data",
        "Upload unlawful, harmful, or abusive content",
        "Use the Service for commercial solicitation without authorization",
      ]} />

      <h2>7. Credits and Subscriptions</h2>
      <p>Heat.ai offers a credit system for messages, voice calls, AI image generation, and gifts. Subscriptions are available for unlimited access. Payments are processed through Apple App Store or Google Play. Deleting your account does not automatically cancel an active subscription — cancellation must be managed through the applicable platform.</p>

      <h2>8. Credits and Purchases</h2>
      <p>Credits purchased are non-refundable except where required by applicable law or platform policy. Unused credits have no cash value and cannot be transferred between accounts.</p>

      <h2>9. Advertising</h2>
      <p>Free users may see ads served via AdMob and mediation networks. Premium subscribers receive an ad-free experience where stated. Ad formats include app-open, interstitial, rewarded, native, and banner ads.</p>

      <h2>10. User Content</h2>
      <p>You may upload profile photos and other content. You retain ownership of your content. By uploading content, you grant us a limited license to host, store, and display it solely to provide the Service. You must not upload content that violates any law or third-party rights.</p>

      <h2>11. AI Memory System</h2>
      <p>The memory system stores conversation details to personalise future interactions. All memory data belongs to you. You may view, edit, delete, or disable the memory system at any time. Privacy controls are never paywalled.</p>

      <h2>12. Termination</h2>
      <p>We may suspend or terminate your access if you violate these Terms, engage in fraud or abuse, or create safety risks. You may delete your account at any time from within the app.</p>

      <h2>13. Disclaimer</h2>
      <p>The Service is provided "AS IS." We do not guarantee that the Service will be uninterrupted or error-free. AI-generated content may be inaccurate. We are not liable for decisions you make based on AI companion responses.</p>

      <h2>14. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, Heat.ai and MacroMatrix (Pvt) Limited will not be liable for indirect, incidental, consequential, or punitive damages arising from your use of the Service.</p>

      <h2>15. Governing Law</h2>
      <p>These Terms are governed by the laws of the Islamic Republic of Pakistan, without limiting any mandatory consumer rights applicable in your jurisdiction.</p>

      <h2>16. Contact</h2>
      <div className="ha-contact-block">
        <p><strong>App Name:</strong> Heat.ai</p>
        <p><strong>Company:</strong> MacroMatrix (Pvt) Limited</p>
        <p><strong>Email:</strong> <a href="mailto:support@macromatrix.io">support@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

export function HeatAiEula() {
  return (
    <div className="ha-legal">
      <SEO title="EULA – Heat.ai" description="End User License Agreement for the Heat.ai AI companion application." path="/heatai/eula" />
      <h1>End User License Agreement (EULA)</h1>
      <p className="ha-meta">Effective Date: {EFFECTIVE_DATE} · Last Updated: {EFFECTIVE_DATE}</p>

      <h2>1. Agreement</h2>
      <p>This EULA is a legal agreement between you and MacroMatrix (Pvt) Limited governing your use of the Heat.ai mobile application (the "Application"). By installing or using the Application, you accept this EULA.</p>

      <h2>2. License Grant</h2>
      <p>We grant you a limited, non-exclusive, non-transferable, revocable license to install and use the Application on a compatible personal device for personal, non-commercial purposes, in accordance with this EULA and applicable laws.</p>

      <h2>3. Restrictions</h2>
      <p>You may not:</p>
      <Ul items={[
        "Copy, distribute, or sell the Application",
        "Reverse engineer or decompile the Application except where law expressly permits",
        "Circumvent security or content filtering systems",
        "Use automated tools to access the Application without authorization",
        "Sublicense or transfer your rights under this EULA",
        "Use the Application for unlawful purposes",
      ]} />

      <h2>4. Age Requirement</h2>
      <p>The Application is strictly for users aged 18 and above. By using the Application, you confirm that you are at least 18 years old.</p>

      <h2>5. AI Content Disclosure</h2>
      <p>All companions in the Application are artificial intelligence. AI-generated responses may be inaccurate. You acknowledge that companions are not real people and that you will not make important life decisions based solely on AI-generated content.</p>

      <h2>6. Intellectual Property</h2>
      <p>The Application, including its software, design, branding, and technology, is owned by MacroMatrix (Pvt) Limited. No ownership rights are transferred to you under this EULA.</p>

      <h2>7. Third-Party Services</h2>
      <p>The Application uses third-party services including Firebase, OpenAI, RevenueCat, AdMob, and AppsFlyer. Your use of these services may be subject to their own terms.</p>

      <h2>8. Updates</h2>
      <p>We may provide updates that modify or improve the Application. Certain updates may be required to continue using the Application.</p>

      <h2>9. Termination</h2>
      <p>This license terminates automatically if you violate this EULA or our Terms of Service. Upon termination, you must stop using and delete the Application.</p>

      <h2>10. Disclaimer</h2>
      <p>The Application is provided "AS IS" without warranties of any kind to the extent permitted by law.</p>

      <h2>11. Governing Law</h2>
      <p>This EULA is governed by the laws of the Islamic Republic of Pakistan.</p>

      <h2>12. Contact</h2>
      <div className="ha-contact-block">
        <p><strong>App Name:</strong> Heat.ai</p>
        <p><strong>Company:</strong> MacroMatrix (Pvt) Limited</p>
        <p><strong>Email:</strong> <a href="mailto:contact@macromatrix.io">contact@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

const FAQS = [
  { q: "What is Heat.ai?", a: "Heat.ai is an AI companion app where you can chat, make voice calls, and build a relationship with AI-powered virtual companions. All companions are artificial intelligence — not real people." },
  { q: "Is Heat.ai free?", a: "Yes — you can use Heat.ai for free with a daily credit allowance. Premium membership unlocks unlimited messages, unlimited voice calls, an ad-free experience, and exclusive companions." },
  { q: "How do voice calls work?", a: "Voice calls use OpenAI's Realtime API for low-latency, natural conversation. Speak normally and your companion responds in real time. Calls consume credits per minute for free users; premium subscribers call without limits." },
  { q: "What is the AI memory system?", a: "Heat.ai remembers details you share in conversation — your name, interests, life events — and weaves them into future replies. You can view, edit, or delete any memory, and turn the system off per companion, any time." },
  { q: "Can I create my own AI companion?", a: "Yes. Under the Custom AI Friends tab, you can design a companion from scratch: choose their name, personality traits, appearance style, and voice. They're then available in your friends list just like preset companions." },
  { q: "What are couple photos and AI videos?", a: "Couple photos are AI-generated images of you and your companion together, generated daily. AI videos let you animate a photo or generate a scene from a text prompt. Free users can watch a rewarded ad to unlock these; premium users get direct access." },
  { q: "Is my data private?", a: "Yes. You can export all your data as a PDF, delete your chat history, and delete individual memories at any time. Privacy controls are never locked behind a paywall. See our Privacy Policy for full details." },
  { q: "How do I cancel my subscription?", a: "Subscriptions are managed through the platform where you purchased them — the App Store (iPhone) or Google Play (Android). Deleting the app or your account does not automatically cancel a subscription." },
  { q: "What age is required?", a: "Heat.ai is strictly for adults aged 18 and above. The app contains mature content. We may suspend accounts we believe belong to minors." },
  { q: "How do I contact support?", a: "Tap the Support link in the navigation or email us at support@macromatrix.io. We aim to respond within 24 hours." },
];

export function HeatAiFaq() {
  const [open, setOpen] = useState(null);
  return (
    <div className="ha-legal">
      <SEO title="FAQ – Heat.ai" description="Frequently asked questions about the Heat.ai AI companion app." path="/heatai/faq" />
      <h1>Frequently Asked Questions</h1>
      <p className="ha-meta">Everything you need to know about Heat.ai.</p>
      <div className="ha-faq-list">
        {FAQS.map((f, i) => (
          <div key={i} className="ha-faq-item">
            <button className="ha-faq-q" onClick={() => setOpen(open === i ? null : i)}>
              {f.q}
              <span>{open === i ? "−" : "+"}</span>
            </button>
            {open === i && <p className="ha-faq-a">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
